import type { ComponentPropsWithRef, MouseEvent } from "react";
import { forwardRef, useCallback, useEffect, useRef } from "react";
import mergeRefs from "merge-refs";
import {
  useBuilderStore,
  useSelectedBlock,
} from "@circle-react-shared/BuilderUI";
import type {
  BlockRendererMapping,
  BlockValues,
  ComponentMeta,
} from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import {
  isChildrenProp,
  isRootBlock,
} from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import type { BlockCategoryName } from "@circle-react-shared/BuilderUI/constants/BlockCategories";
import { isNonNullableUnknown } from "../Helpers/typePredicates";
import { PreviewBlockList } from "./PreviewBlockList";

interface AcceptsRejectsConfig {
  accepts: BlockCategoryName[];
  rejects: BlockCategoryName[];
}

const getAcceptsRejectsConfig = (
  blockMeta: ComponentMeta,
  propName: string,
): AcceptsRejectsConfig => {
  const metaProp = blockMeta.props?.[propName];
  const accepts =
    (metaProp && metaProp.prop === "children" ? metaProp.accepts : undefined) ??
    [];
  const rejects =
    (metaProp && metaProp.prop === "children" ? metaProp.rejects : undefined) ??
    [];
  return { accepts, rejects };
};

export interface PreviewProps extends ComponentPropsWithRef<"div"> {
  block: BaseBlock;
  components: BlockRendererMapping;
  path: string;
}

export const Preview = forwardRef<HTMLElement, PreviewProps>(
  ({ block, components, path, className, ...externalProps }, forwardedRef) => {
    const scrollRef = useRef<HTMLElement>(null);
    const { selectedBlock, setSelectedBlockPath } = useSelectedBlock();
    const getBlockMeta = useBuilderStore(state => state.getBlockMeta);
    const closeSidebarView = useBuilderStore(state => state.closeSidebarView);
    const isFullscreenPreview = useBuilderStore(
      state => state.isFullscreenPreview,
    );

    const selectedBlockId = selectedBlock?.id;
    const currentBlockId = block.id;
    const externalOnClick = externalProps.onClick;

    const handleClick = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        externalOnClick?.(event);
        event.stopPropagation();
        setSelectedBlockPath(path);
        closeSidebarView();
      },
      [externalOnClick, path, setSelectedBlockPath, closeSidebarView],
    );

    useEffect(() => {
      if (isFullscreenPreview) return;
      const element = scrollRef.current;
      if (selectedBlockId && selectedBlockId === currentBlockId && element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, [selectedBlockId, currentBlockId, isFullscreenPreview]);

    const PreviewComponent = components[block.type];
    const blockMeta = getBlockMeta(block.type);
    if (!PreviewComponent || !blockMeta) return null;

    const entries = Object.entries(block.props);
    const shouldNotAddEvents = isRootBlock(block);
    const ref = mergeRefs(scrollRef, forwardedRef);

    const blockProps = entries.reduce<BlockValues>((props, [name, prop]) => {
      if (isChildrenProp(prop)) {
        const { accepts, rejects } = getAcceptsRejectsConfig(blockMeta, name);

        props[name] = (
          <PreviewBlockList
            accepts={accepts}
            rejects={rejects}
            blocks={prop.value}
            components={components}
            path={`${path}.props.${name}.value`}
          />
        );
      } else if (isNonNullableUnknown(prop.value)) {
        props[name] = prop.value;
      }
      return props;
    }, {});

    if (shouldNotAddEvents) {
      return (
        <PreviewComponent
          data-preview-mode
          data-block
          data-block-type={block.type}
          {...blockProps}
        />
      );
    }

    return (
      <PreviewComponent
        {...blockProps}
        {...externalProps}
        key={currentBlockId}
        id={currentBlockId}
        ref={ref}
        data-preview-mode
        data-block
        data-block-type={block.type}
        onClick={handleClick}
        className={className}
      />
    );
  },
);

Preview.displayName = "Preview";
